import type {
  BrandSummary,
  CategorySummary,
  ComparisonDetail,
  FilterOption,
  FinderConfig,
  FinderResult,
  PaginatedResponse,
  ProductDetail,
  ProductSummary,
} from './types';

/**
 * Server components (SSR) reach the backend over the internal Docker network
 * (INTERNAL_API_URL); the browser must use a host-reachable URL
 * (NEXT_PUBLIC_API_URL). See docker-compose.yml for both values.
 */
function apiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return process.env.INTERNAL_API_URL ?? process.env.NEXT_PUBLIC_API_URL ?? 'http://backend-nginx/api/v1';
  }

  return process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8010/api/v1';
}

async function apiFetch<T>(path: string, searchParams?: URLSearchParams): Promise<T> {
  const url = new URL(apiBaseUrl() + path);
  if (searchParams) {
    searchParams.forEach((value, key) => url.searchParams.append(key, value));
  }

  const response = await fetch(url.toString(), {
    headers: { Accept: 'application/json' },
    next: { revalidate: 60 },
  });

  if (!response.ok) {
    throw new Error(`CasaMetric API ${response.status}: ${path}`);
  }

  return response.json() as Promise<T>;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(apiBaseUrl() + path, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`CasaMetric API ${response.status}: ${path}`);
  }

  return response.json() as Promise<T>;
}

export const api = {
  categories: {
    list: () => apiFetch<{ data: CategorySummary[] }>('/categories'),
    show: (slug: string) => apiFetch<{ data: CategorySummary }>(`/categories/${slug}`),
    filters: (slug: string) => apiFetch<{ data: FilterOption[] }>(`/categories/${slug}/filters`),
  },
  brands: {
    list: () => apiFetch<PaginatedResponse<BrandSummary>>('/brands'),
    show: (slug: string) => apiFetch<{ data: BrandSummary }>(`/brands/${slug}`),
  },
  products: {
    list: (params: Record<string, string> = {}) =>
      apiFetch<PaginatedResponse<ProductSummary>>('/products', new URLSearchParams(params)),
    show: (slug: string) => apiFetch<{ data: ProductDetail }>(`/products/${slug}`),
  },
  comparisons: {
    list: () => apiFetch<{ data: string[] }>('/comparisons'),
    show: (slug: string) => apiFetch<{ data: ComparisonDetail }>(`/comparisons/${slug}`),
  },
  finder: {
    show: (categorySlug: string) => apiFetch<{ data: FinderConfig }>(`/finder/${categorySlug}`),
    submit: (categorySlug: string, answers: Record<number, unknown>) =>
      apiPost<{ data: FinderResult }>(`/finder/${categorySlug}/submit`, { answers }),
  },
};
